import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import styled from '@emotion/styled'
import { MediaQueries, theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { ChevronLeft, ChevronRight } from '@hhgtech/icons/arrow'
import { Carousel, Embla } from '@mantine/carousel'
import { Box, clsx, Flex, rem } from '@mantine/core'
import innertext from 'innertext'

import { formatArticle } from 'api/dataTransform'
import { getHelloSitesUrl } from 'api/utils/paths'
import { CardArticle } from 'components/atoms/CardArticleV2'
import { CardArticleAuthorName } from 'components/atoms/CardArticleV2/CardArticleAuthor'
import { NextImageWrap } from 'components/atoms/imageWrap'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath, DEFAULT_IMG } from 'interfaces/constants'
import { TrimesterAdditionArticle } from 'interfaces/types/DueDate'
import { getAuthorFromArticleApi } from 'utils/author'

const SECONDS_TO_CHANGE_SLIDE = 14

const StyledCardArticle = styled(CardArticle)`
  &.cardArticle {
    &__horizontal {
      grid-template-columns: 1fr ${rem(80)};
      .cardArticleImageRatio {
        &:before {
          padding-bottom: 100%;
        }
      }
    }
  }

  ${MediaQueries.mbDown} {
    &.cardArticle {
      &__horizontal {
      }
      .cardArticleAuthorLabel {
        font-size: ${rem(12)};
      }
    }
  }
`

export type OtherAritlcesProps = {
  title?: string
  description?: string
  articles?: TrimesterAdditionArticle[]
  className?: string
  style?: CSSProperties
}
export const OtherAritlces: FC<OtherAritlcesProps> = (props) => {
  const {
    title,
    description,
    articles: articlesProps,
    className,
    style,
  } = props

  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()
  const progressBarRef = useRef<HTMLDivElement>(null)
  const [embla, setEmbla] = useState<Embla>()
  const [currentSlide, setCurrentSlide] = useState(0)

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const articles = useMemo(() => {
    if (!articlesProps || !articlesProps.length) {
      return []
    }
    const articles = []
    let i = 0
    const OFFSET = isMobile ? 4 : 3
    while (i < articlesProps.length) {
      articles.push(articlesProps.slice(i, i + OFFSET))
      i += OFFSET
    }
    return articles
  }, [articlesProps, isMobile])
  const moreThanOneSlide = articles.length > 1

  const handleScroll = useCallback(() => {
    if (!embla) return
    setCurrentSlide(embla.selectedScrollSnap())
  }, [embla])

  const triggerProgressBar = useCallback(() => {
    // Reset to zero first
    if (progressBarRef.current) {
      progressBarRef.current.style.transition = 'none'
      progressBarRef.current.style.width = '0'
    }
    requestAnimationFrame(() => {
      // Then trigger animation to 100%
      if (progressBarRef.current) {
        progressBarRef.current.style.transition = `all ${SECONDS_TO_CHANGE_SLIDE}s linear`
        progressBarRef.current.style.width = '100%'
      }
    })
  }, [])

  useEffect(() => {
    if (!embla || !moreThanOneSlide) return

    triggerProgressBar()
    const intervalId = setInterval(() => {
      triggerProgressBar()
      embla.scrollNext()
    }, SECONDS_TO_CHANGE_SLIDE * 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [
    embla,
    currentSlide,
    triggerProgressBar,
    moreThanOneSlide,
    Boolean(progressBarRef.current),
  ])

  useEffect(() => {
    if (!embla) {
      return
    }

    embla.on('scroll', handleScroll)
    return () => {
      embla.off('scroll', handleScroll)
    }
  }, [embla])

  if (!articles.length) {
    return null
  }

  return (
    <Box
      className={clsx(
        className,
        'max-md:py-6 max-md:px-4 max-md:bg-pink50 max-md:rounded-2xl',
      )}
      style={style}
    >
      <Text
        as="h3"
        size="s1"
        color={theme.colors.gray800}
        weight="semiBold"
        className="mb-4 md:mb-2"
      >
        {title}
      </Text>
      <Text as="p" size="p4" color={theme.colors.gray600}>
        {description}
      </Text>

      <Carousel
        loop
        draggable={false}
        dragFree={false}
        slideGap={24}
        withControls={false}
        getEmblaApi={setEmbla}
        className="mt-4 md:mt-2"
      >
        {articles.map((posts, index) => {
          return (
            <Carousel.Slide key={`${index}`}>
              <Box className="md:bg-white md:p-4 md:rounded-2xl">
                {posts.map((post, postIdx) => {
                  const article = formatArticle(post)
                  const author = getAuthorFromArticleApi({
                    article: post,
                    locale,
                    version: 'short',
                  })

                  return (
                    <Box
                      key={`${article.id}-${postIdx}`}
                      className="border-solid border-t border-neutral100 py-4 md:first:pt-0 md:first:border-none"
                    >
                      <StyledCardArticle
                        type="horizontal"
                        item={{
                          ID: parseInt(`${article.id}`),
                          image: (
                            <NextImageWrap
                              src={article.featureImage || DEFAULT_IMG}
                              fill
                              alt={article.postName}
                            />
                          ),
                          permalink: article.permalink,
                          title: innertext(
                            article.postTitle || article.postName || '',
                          ),
                          category: article.category,
                          author: {
                            name: <CardArticleAuthorName {...author} />,
                            permalink: author.avatar_permalink,
                            avatarAlt: author.avatar_alt,
                            avatar: author.avatar,
                            indicator: author.indicator,
                          },
                        }}
                        itemTracking={{
                          image: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Other Article',
                            'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                          },
                          title: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Other Article',
                            'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                          },
                          category: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Other Article Category',
                            'data-event-label': `${currentDomain}${basePath}${article.category.permalinkDecode}`,
                          },
                          authorAvatar: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click See Doctor',
                            'data-event-label': `${currentDomain}${basePath}${author.avatar_permalink}`,
                          },
                        }}
                        bookmarkButtonProps={{
                          style: { display: 'none' },
                        }}
                      />
                    </Box>
                  )
                })}
              </Box>
            </Carousel.Slide>
          )
        })}
      </Carousel>
      {moreThanOneSlide && (
        <Flex mt={isMobile ? 16 : 24} gap={24} align="center">
          <Text size="p2" weight="semiBold" color={theme.colors.gray400}>
            {String(currentSlide + 1).padStart(2, '0')}/
            {String(articles.length).padStart(2, '0')}
          </Text>
          <Box sx={{ flex: 1 }} bg={theme.colors.pink50}>
            <Box
              ref={progressBarRef}
              h={1}
              w={moreThanOneSlide ? 0 : '100%'}
              bg={theme.colors.pink800}
            />
          </Box>
          <Flex direction="row" gap={16}>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={theme.colors.white}
              sx={{ borderRadius: '100%', cursor: 'pointer' }}
              onClick={() => embla?.scrollPrev()}
            >
              <ChevronLeft size={20} color={theme.colors.gray600} />
            </Flex>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={theme.colors.white}
              sx={{ borderRadius: '100%', cursor: 'pointer' }}
              onClick={() => embla?.scrollNext()}
            >
              <ChevronRight size={20} color={theme.colors.gray600} />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Box>
  )
}
